import { generatePdf } from "@/api";
import AdjustmentSteps from "@/components/AdjustmentSteps";
import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import BackToDashboard from "@/components/BackToDashboard";
import LoadingCard from "@/components/LoadingCard";
import { PropertyHeader } from "@/components/PropertyHeader";
import { RegressionModelDetails } from "@/components/RegressionModelDetails";
import ResponsiveContainer from "@/components/ResponsiveContainer";
import { CompAdjustmentResult, ScatterplotSummary } from "@/components/ScatterPlotSummary";
import StepTitleSection from "@/components/StepTitleSection";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useAutosizeTextArea } from "@/hooks/useAutoresizeTextArea";
import useFetch from "@/hooks/useFetch";
import { deriveChartStuff } from "@/lib/deriveChartStuff";
import { formatDate, formatPrice } from "@/lib/formatters";
import { normalizeDataBasedOnVersion } from "@/lib/PreparedDataset";
import { PageProps } from "@/types/PageProps";
import RegressionModel from "@/types/RegressionModel";
import { Adjustment } from "@/types/serializers";
import { Head, router } from "@inertiajs/react";
import { VariantProps } from "class-variance-authority";
import { parseCalendarDate } from "@/lib/calendarDate";
import { CheckIcon, DownloadIcon, Loader2Icon } from "lucide-react";
import { useRef, useState } from "react";
import { GrHelpBook } from "react-icons/gr";

type Comp = { id: string };
type Props = PageProps & {
  adjustment: Adjustment;
  dataset_url: string | null;
  regression_model: RegressionModel | null;
  comps: Comp[];
  outliers: string[];
};

function CompAdjustments({
  comps,
  compAdjustmentResults,
}: {
  comps: Comp[];
  compAdjustmentResults: CompAdjustmentResult[];
}) {
  return (
    <div>
      <div className="font-bold pb-2">Comp Adjustments</div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Address</TableHead>
            <TableHead>Contract Date</TableHead>
            <TableHead className="text-right">Close Price</TableHead>
            <TableHead className="text-right">Calculated Effective Date Price</TableHead>
            <TableHead className="text-right">Adjustment</TableHead>
            <TableHead className="text-right">Percent Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {comps.map((comp) => {
            const result = compAdjustmentResults.find((r) => r.id === comp.id);
            if (!result) return null;

            return (
              <TableRow key={comp.id}>
                <TableCell>{result.address}</TableCell>
                <TableCell>{formatDate(result.saleDate.getTime())}</TableCell>
                <TableCell className="text-right">{formatPrice(result.salePrice)}</TableCell>
                <TableCell className="text-right">{formatPrice(result.adjustedValue)}</TableCell>
                {result.adjustmentAmount < 0 ? (
                  <TableCell className="text-right">
                    <span className="text-red-600 font-bold">
                      {formatPrice(result.adjustmentAmount)}
                    </span>
                  </TableCell>
                ) : (
                  <TableCell className="text-right">
                    <span className="text-green-600 font-bold">
                      +{formatPrice(result.adjustmentAmount)}
                    </span>
                  </TableCell>
                )}
                {result.percentChange < 0 ? (
                  <TableCell className="text-right">{result.percentChange.toFixed(1)}%</TableCell>
                ) : (
                  <TableCell className="text-right">{result.percentChange.toFixed(1)}%</TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function AppraiserNotes({ adjustment }: { adjustment: Adjustment }) {
  // track whether there are unsaved changes
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState(adjustment.appraiser_notes === null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(adjustment.appraiser_notes || "");

  useAutosizeTextArea(textAreaRef.current, value);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  if (!editing) {
    return (
      <div className="space-y-2">
        <div className="font-bold pb-2">
          Appraiser Notes{" "}
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </div>

        <div className="text-sm whitespace-pre-wrap">{adjustment.appraiser_notes}</div>
      </div>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const appraiser_notes = formData.get("appraiser_notes") as string;
        setIsSaving(true);
        await router.patch(
          `/adjustments/${adjustment.public_id}/summary/update`,
          {
            appraiser_notes,
          },
          {
            preserveScroll: true,
          },
        );
        setIsSaving(false);
        setEditing(false);
      }}
    >
      <div className="space-y-2">
        <div className="font-bold pb-2">Appraiser Notes</div>

        <div className="text-sm">
          <Textarea
            ref={textAreaRef}
            className="overflow-hidden min-h-[150px]"
            name="appraiser_notes"
            placeholder="We recommend including the selection criteria for the MLS data that you included, as well any additional contextual information."
            defaultValue={adjustment.appraiser_notes}
            onChange={handleChange}
          />
        </div>
        <div className="flex justify-end">
          <Button className="w-48" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save "}
          </Button>
        </div>
      </div>
    </form>
  );
}

function TrendMethodology({ regression_model }: { regression_model: RegressionModel }) {
  return (
    <div>
      <div className="font-bold pb-2">Trend Methodology</div>
      <RegressionModelDetails model={regression_model} className="text-sm" />
    </div>
  );
}

type DownloadPdfButtonProps = VariantProps<typeof buttonVariants> & {
  adjustment: Adjustment;
  disabled: boolean;
  children: React.ReactNode;
};

function DownloadPdfButton({ variant, adjustment, disabled, children }: DownloadPdfButtonProps) {
  const [pdfGenerating, setPdfGenerating] = useState(false);

  return (
    <Button
      variant={variant}
      onClick={async () => {
        setPdfGenerating(true);

        try {
          const response = await generatePdf(adjustment.public_id);
          const fileUrl = response.file_url;

          setPdfGenerating(false);
          // prompt the browser to download the file
          window.open(fileUrl, "_blank");

          router.reload();
        } catch (error) {
          setPdfGenerating(false);
          toast({
            title: "A problem occurred while generating the PDF",
            description:
              "Please try again later, or contact support@teeba-calculator.com if the issue persists.",
            variant: "destructive",
          });
          console.error(error);
        }
      }}
      disabled={pdfGenerating || disabled}
    >
      {pdfGenerating ? (
        <span className="flex items-center">
          Generating PDF <Loader2Icon className="ml-2 animate-spin" />
        </span>
      ) : (
        <span className="flex items-center">{children}</span>
      )}{" "}
    </Button>
  );
}

export default function Component({
  user,
  adjustment,
  errors,
  notice,
  dataset_url,
  comps,
  outliers,
  regression_model,
}: Props) {
  const { data, isPending, error } = useFetch(dataset_url);

  const scatterplotData = data
    ? normalizeDataBasedOnVersion(data, outliers, comps).filter((d) => !d.outlier)
    : [];

  if (isPending) {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title={`Summary - ${adjustment.address}`} />
        <div className="py-4">
          <BackToDashboard />
          <AdjustmentSteps currentStep="summary" adjustment={adjustment} className="mb-4" />
          <LoadingCard />
        </div>
      </AuthenticatedLayout>
    );
  }
  if (scatterplotData.length === 0) {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title={`Summary - ${adjustment.address}`} />
        <div className="py-4">
          <BackToDashboard />
          <AdjustmentSteps currentStep="summary" adjustment={adjustment} className="mb-4" />
          <div className="pt-8 text-center">No data found</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  const chartStuff =
    adjustment.effective_date && adjustment.address && scatterplotData.length > 0
      ? deriveChartStuff(scatterplotData, regression_model, {
          date: parseCalendarDate(adjustment.effective_date),
          name: adjustment.address || "",
        })
      : null;

  const compAdjustmentResults = chartStuff?.compAdjustmentResults;

  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title={`Summary - ${adjustment.address}`} />
      <div className="py-4">
        <BackToDashboard />

        <AdjustmentSteps currentStep="summary" adjustment={adjustment} className="mb-4" />

        <div className="flex pt-4 space-x-8 mx-auto">
          <div className="flex-grow max-w-5xl">
            <div className="w-full">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Market Condition Adjustments Summary</CardTitle>
                </CardHeader>

                <CardContent>
                  <PropertyHeader adjustment={adjustment} />

                  <div className="pt-8">
                    <div className="w-full space-y-4">
                      <div className="space-y-8">
                        <CompAdjustments
                          comps={comps}
                          compAdjustmentResults={compAdjustmentResults || []}
                        />

                        <div className="lg:flex lg:space-x-8 space-y-4 lg:space-y-0">
                          <div className="flex-1">
                            <AppraiserNotes adjustment={adjustment} />
                          </div>
                          <div className="flex-1">
                            {regression_model && (
                              <TrendMethodology regression_model={regression_model} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-6">
                    <div className="font-bold pb-2">Comparable Home Sales Trend</div>
                    <div className="w-full">
                      <Card>
                        <CardContent className="pt-6">
                          {isPending && <div>Loading...</div>}
                          {error && <div>Error: {error}</div>}
                          {!isPending && !error && scatterplotData && (
                            <ResponsiveContainer aspectRatio={16 / 6} maxHeight={600}>
                              <ScatterplotSummary
                                width={700}
                                height={500}
                                data={scatterplotData}
                                regressionType={regression_model}
                                onPointClick={() => {}}
                                subject={
                                  adjustment.effective_date && adjustment.address
                                    ? {
                                        date: parseCalendarDate(adjustment.effective_date),
                                        name: adjustment.address || "",
                                      }
                                    : undefined
                                }
                                chartStuff={chartStuff}
                              />
                            </ResponsiveContainer>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className="max-w-md flex-grow">
            <div className="w-full space-y-4">
              <div className="space-y-4"></div>
              <StepTitleSection
                title="Adjustment Summary"
                description={
                  <div className="space-y-4">
                    <div>
                      Check to make sure your adjustments make sense. Be sure to add context in the
                      appraiser notes section.
                    </div>

                    <div>
                      <Alert variant="muted">
                        <GrHelpBook className="w-5 h-5" />
                        <AlertTitle>Need some help?</AlertTitle>
                        <AlertDescription>
                          <a
                            href="https://teeba-calculator.com/instructions#summary"
                            className="text-primary font-bold hover:underline"
                            target="_blank"
                          >
                            Click here
                          </a>{" "}
                          to view more detailed instructions.
                        </AlertDescription>
                      </Alert>
                    </div>
                  </div>
                }
              >
                {adjustment.summary_page_status === "completed" ? (
                  <div className="pt-4 text-sm">
                    Completed and saved <CheckIcon className="inline-block w-4 h-4 align-middle" />
                  </div>
                ) : null}

                <div className="flex flex-col gap-2">
                  {adjustment.summary_page_status === "started" ? (
                    <DownloadPdfButton adjustment={adjustment} disabled={false}>
                      Complete and Download PDF <DownloadIcon className="ml-2" />
                    </DownloadPdfButton>
                  ) : (
                    <DownloadPdfButton variant="outline" adjustment={adjustment} disabled={false}>
                      Download PDF <DownloadIcon className="ml-2" />
                    </DownloadPdfButton>
                  )}
                </div>
              </StepTitleSection>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
